/**
 * Story 062 — View / Edit component-model modal
 * (`/component-models?model=<uuid>`).
 *
 * R58-CC-02e: the read-only / role-gating UI is delegated to the shared
 * `<ReadOnlyModal>` primitive in `@aeriosiq/ui`. The
 * `resolveModalMode` helper (which combines roles × scope × isActive)
 * stays local; its result is routed through the primitive's `readOnly`
 * + `bannerOverride` props.
 *
 * Component-Group switch flow (Story 062 AC16) — the user picks a
 * different group → confirmation dialog → on Confirm the Specifications
 * array re-seeds from the new group's defaults — is preserved verbatim.
 *
 * Audit visibility (Christian's Q11 amendment): tenant-scope rows show
 * createdAt/By, updatedAt/By, deletedAt/By; global-scope rows hide the
 * audit block entirely.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  ReadOnlyModal,
  ScopeChip,
  defaultsForGroupCode,
  useReadOnlyContext,
} from "@aeriosiq/ui";
import { useFreshRoles } from "../../sites/shared/useFreshRoles.js";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import {
  useDeactivateComponentModelMutation,
  useGetComponentModelQuery,
  useListComponentGroupsQuery,
  useReactivateComponentModelMutation,
  useUpdateComponentModelMutation,
  type AssetComponentModelDetail,
  type AssetComponentModelExternalId,
  type AssetComponentModelSpecification,
  type ComponentGroup,
  type UpdateAssetComponentModelBody,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import { SpecificationsField } from "../shared/SpecificationsField.js";
import { resolveModalMode } from "../shared/modalMode.js";

interface EditComponentModelFormValues {
  name: string;
  category: string;
  componentGroupId: string;
  manufacturer: string;
  manufacturerAbbreviation: string;
  specifications: AssetComponentModelSpecification[];
  externalIds: AssetComponentModelExternalId[];
  description: string;
}

function detailToFormValues(
  d: AssetComponentModelDetail,
): EditComponentModelFormValues {
  return {
    name: d.name.en ?? "",
    category: d.category ?? "",
    componentGroupId: d.componentGroupId,
    manufacturer: d.manufacturer ?? "",
    manufacturerAbbreviation: d.manufacturerAbbreviation ?? "",
    specifications: d.specifications.map((s) => ({
      parameter: s.parameter,
      unit: s.unit,
      value: s.value,
    })),
    externalIds: d.externalIds.map((e) => ({
      provider: e.provider,
      id: e.id,
    })),
    description: d.description.en ?? "",
  };
}

function dropEmptySpecifications(
  rows: AssetComponentModelSpecification[],
): AssetComponentModelSpecification[] {
  return rows
    .map((r) => ({
      parameter: r.parameter.trim(),
      unit: r.unit.trim(),
      value: r.value.trim(),
    }))
    .filter((r) => r.parameter.length > 0);
}

function dropEmptyExternalIds(
  arr: AssetComponentModelExternalId[],
): AssetComponentModelExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildUpdateBody(
  v: EditComponentModelFormValues,
): UpdateAssetComponentModelBody {
  return {
    name: { en: v.name.trim() },
    category: v.category.trim(),
    componentGroupId: v.componentGroupId,
    manufacturer:
      v.manufacturer.trim().length > 0 ? v.manufacturer.trim() : null,
    manufacturerAbbreviation:
      v.manufacturerAbbreviation.trim().length > 0
        ? v.manufacturerAbbreviation.trim()
        : null,
    specifications: dropEmptySpecifications(v.specifications),
    externalIds: dropEmptyExternalIds(v.externalIds),
    description: { en: v.description.trim() },
  };
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}

export interface EditComponentModelDialogProps {
  modelId: string | null | undefined;
  onClose: () => void;
}

export function EditComponentModelDialog({
  modelId,
  onClose,
}: EditComponentModelDialogProps): ReactElement {
  const open = typeof modelId === "string" && modelId.length > 0;
  const {
    data: detail,
    isPending,
    isError,
  } = useGetComponentModelQuery(modelId);

  const componentGroupsQuery = useListComponentGroupsQuery();
  const componentGroups: readonly ComponentGroup[] = useMemo(
    () => componentGroupsQuery.data?.items ?? [],
    [componentGroupsQuery.data?.items],
  );
  const componentGroupById = useMemo(() => {
    const m = new Map<string, ComponentGroup>();
    for (const g of componentGroups) m.set(g.id, g);
    return m;
  }, [componentGroups]);

  const roles = useFreshRoles();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isDirty, isSubmitting },
  } = useForm<EditComponentModelFormValues>({
    defaultValues: {
      name: "",
      category: "",
      componentGroupId: "",
      manufacturer: "",
      manufacturerAbbreviation: "",
      specifications: [],
      externalIds: [],
      description: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (detail) {
      reset(detailToFormValues(detail));
    }
  }, [detail, reset]);

  const update = useUpdateComponentModelMutation();
  const deactivate = useDeactivateComponentModelMutation();
  const reactivate = useReactivateComponentModelMutation();
  const { toast } = useToast();

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);

  const resolved = detail
    ? resolveModalMode(roles, detail.scope, detail.isActive)
    : {
        mode: "view" as const,
        canSave: false,
        canDeactivate: false,
        canReactivate: false,
        banner: null,
      };
  const readOnly = resolved.mode === "view";
  const isInactive = detail?.isActive === false;

  function seedSpecsFromGroupId(nextGroupId: string): void {
    const group = componentGroupById.get(nextGroupId);
    const defaults = defaultsForGroupCode(group?.groupCode);
    setValue(
      "specifications",
      defaults.map((d) => ({ ...d })),
      { shouldDirty: true, shouldValidate: false },
    );
  }

  function handleComponentGroupChange(nextGroupId: string | null): void {
    if (readOnly) return;
    if (!nextGroupId) {
      setValue("componentGroupId", "", { shouldDirty: true });
      return;
    }
    const current = getValues("componentGroupId");
    if (nextGroupId === current) return;
    const hasExistingSpecs = getValues("specifications").length > 0;
    if (hasExistingSpecs && current.length > 0) {
      setPendingGroupId(nextGroupId);
      return;
    }
    setValue("componentGroupId", nextGroupId, {
      shouldDirty: true,
      shouldValidate: true,
    });
    seedSpecsFromGroupId(nextGroupId);
  }

  function confirmGroupSwap(): void {
    if (!pendingGroupId) return;
    setValue("componentGroupId", pendingGroupId, {
      shouldDirty: true,
      shouldValidate: true,
    });
    seedSpecsFromGroupId(pendingGroupId);
    setPendingGroupId(null);
  }
  function cancelGroupSwap(): void {
    setPendingGroupId(null);
  }

  async function onSubmit(v: EditComponentModelFormValues): Promise<void> {
    if (!detail) return;
    try {
      await update.mutateAsync({
        id: detail.id,
        body: buildUpdateBody(v),
      });
      toast("Component model updated.");
      onClose();
    } catch (err) {
      const errBody = (err as { body?: { code?: string; detail?: string } })
        .body;
      if (errBody?.detail?.includes("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else if (errBody?.code === "forbidden") {
        toast(
          "You don't have permission to edit this component model.",
          "error",
        );
      } else {
        toast("Couldn't save component model. Try again.", "error");
      }
    }
  }

  async function doDeactivate(): Promise<void> {
    if (!detail) return;
    try {
      await deactivate.mutateAsync(detail.id);
      toast(`Component model ${detail.name.en ?? ""} deactivated.`);
      setConfirmDeactivate(false);
      onClose();
    } catch {
      toast("Couldn't deactivate component model.", "error");
    }
  }

  async function doReactivate(): Promise<void> {
    if (!detail) return;
    try {
      await reactivate.mutateAsync(detail.id);
      toast(`Component model ${detail.name.en ?? ""} reactivated.`);
      setConfirmReactivate(false);
      onClose();
    } catch {
      toast("Couldn't reactivate component model.", "error");
    }
  }

  const title =
    resolved.mode === "edit" ? "Edit component model" : "View component model";

  const showAudit = detail?.scope === "tenant";

  const subtitle = useMemo(() => {
    if (!detail) return "";
    const scopeLabel = detail.scope === "global" ? "Global" : "Tenant";
    const group = componentGroupById.get(detail.componentGroupId);
    const groupName = group?.name.en ?? group?.groupCode ?? "—";
    return `${groupName} · ${scopeLabel} · code ${detail.assetComponentModelCode}`;
  }, [detail, componentGroupById]);

  const usedBy = detail?.usedByComponentCount ?? 0;
  const usedByText = `Used by ${String(usedBy)} component${usedBy === 1 ? "" : "s"}`;

  const titleNode = (
    <>
      <Typography
        id="edit-component-model-heading"
        component="span"
        variant="h6"
        sx={{ display: "block" }}
      >
        {title}
      </Typography>
      {detail ? (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </>
  );

  // Banner copy resolution — inactive > global-scope > role-deny (default).
  //
  // SB2-CC-05 — global-scope banner copy tightened to match
  // Christian's [00:21:23] lock. See the matching block in
  // EditTurbineModelDialog for the rationale.
  const bannerOverride = readOnly
    ? isInactive
      ? "This component model is inactive. Reactivate to edit."
      : resolved.banner === "global"
        ? "This is a global component model. Only platform admin can modify."
        : undefined
    : undefined;

  const actionsNode = detail ? (
    <>
      <Button onClick={onClose} disabled={update.isPending || isSubmitting}>
        Cancel
      </Button>
      {!detail.isActive && resolved.canReactivate ? (
        <Button
          variant="contained"
          onClick={() => {
            setConfirmReactivate(true);
          }}
          disabled={reactivate.isPending}
          startIcon={
            reactivate.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Reactivate
        </Button>
      ) : null}
      {detail.isActive && resolved.canDeactivate ? (
        <Button
          color="error"
          onClick={() => {
            setConfirmDeactivate(true);
          }}
        >
          Deactivate
        </Button>
      ) : null}
      {detail.isActive && resolved.canSave ? (
        <>
          <Box sx={{ flex: 1 }} />
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            disabled={!isDirty || update.isPending}
            startIcon={
              update.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Save changes
          </Button>
        </>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <ReadOnlyModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-component-model-heading"
        readOnly={readOnly}
        entityNoun="component model"
        {...(bannerOverride ? { bannerOverride } : {})}
        title={titleNode}
        actions={actionsNode}
        showActionsInReadOnly={
          readOnly && !!detail && !detail.isActive && resolved.canReactivate
        }
      >
        {isPending ? (
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : isError ? (
          <Alert severity="error">
            Couldn&apos;t load this component model.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Identity
            </Typography>
            <TextField
              label="Model ID"
              value={detail.id}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Model ID",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <TextField
              label="Component Model Code"
              value={detail.assetComponentModelCode}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Component Model Code",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Scope:
              </Typography>
              <ScopeChip scope={detail.scope} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {usedByText}
            </Typography>

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Details
            </Typography>

            <TextField
              {...register("name", { required: true })}
              label="Name"
              required
              slotProps={{
                htmlInput: {
                  "aria-label": "Name",
                  "aria-required": true,
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  label="Category"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  required
                  slotProps={{
                    htmlInput: {
                      "aria-label": "Category",
                      "aria-required": true,
                      maxLength: 100,
                    },
                  }}
                  fullWidth
                />
              )}
            />
            <Controller
              name="componentGroupId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const selected =
                  componentGroups.find((g) => g.id === field.value) ?? null;
                return (
                  // SB2-CC-03 — context-aware inline Autocomplete.
                  <ComponentGroupInlineSelect
                    componentGroups={componentGroups}
                    selected={selected}
                    onChange={(next) => {
                      handleComponentGroupChange(next ? next.id : null);
                    }}
                  />
                );
              }}
            />
            <Controller
              name="manufacturer"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Manufacturer"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  slotProps={{
                    htmlInput: {
                      "aria-label": "Manufacturer",
                      maxLength: 100,
                    },
                  }}
                  fullWidth
                />
              )}
            />
            <TextField
              {...register("manufacturerAbbreviation")}
              label="Manufacturer abbreviation"
              slotProps={{
                htmlInput: {
                  "aria-label": "Manufacturer abbreviation",
                  maxLength: 20,
                },
              }}
              fullWidth
            />

            <SpecificationsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <ExternalIdsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <TextField
              {...register("description")}
              label="Description (EN)"
              multiline
              rows={3}
              slotProps={{
                htmlInput: {
                  "aria-label": "Description",
                  maxLength: 2000,
                },
              }}
              fullWidth
            />

            {showAudit ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Audit
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Created at"
                    value={formatIso(detail.createdAt)}
                    variant="standard"
                    slotProps={{
                      htmlInput: { readOnly: true, tabIndex: -1 },
                    }}
                    fullWidth
                  />
                  <AuditPersonField
                    label="Created by"
                    person={detail.createdBy}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Updated at"
                    value={formatIso(detail.updatedAt)}
                    variant="standard"
                    slotProps={{
                      htmlInput: { readOnly: true, tabIndex: -1 },
                    }}
                    fullWidth
                  />
                  <AuditPersonField
                    label="Updated by"
                    person={detail.updatedBy}
                  />
                </Stack>
                {detail.deletedAt ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Deleted at"
                      value={formatIso(detail.deletedAt)}
                      variant="standard"
                      slotProps={{
                        htmlInput: { readOnly: true, tabIndex: -1 },
                      }}
                      fullWidth
                    />
                    <AuditPersonField
                      label="Deleted by"
                      person={detail.deletedBy}
                    />
                  </Stack>
                ) : null}
              </>
            ) : null}
          </Stack>
        )}
      </ReadOnlyModal>

      {/* Deactivate confirmation (AC12). */}
      <ConfirmDialog
        open={confirmDeactivate}
        title={`Confirm: Deactivate component model "${detail?.name.en ?? ""}"?`}
        description={
          <Box>
            This component model is referenced by{" "}
            <strong>
              {String(usedBy)} component{usedBy === 1 ? "" : "s"}
            </strong>
            . Deactivating hides it from the default catalogue but the
            components that currently reference it stay intact. You can{" "}
            <strong>reactivate</strong> it later from the Show inactive view.
          </Box>
        }
        confirmLabel="Confirm"
        danger
        pending={deactivate.isPending}
        onConfirm={() => {
          void doDeactivate();
        }}
        onCancel={() => {
          setConfirmDeactivate(false);
        }}
      />

      {/* Reactivate confirmation (AC23). */}
      <ConfirmDialog
        open={confirmReactivate}
        title={`Confirm: Reactivate component model "${detail?.name.en ?? ""}"?`}
        description={
          <Box>
            This model will reappear in the default catalogue and become
            selectable when adding new components.
          </Box>
        }
        confirmLabel="Confirm"
        pending={reactivate.isPending}
        onConfirm={() => {
          void doReactivate();
        }}
        onCancel={() => {
          setConfirmReactivate(false);
        }}
      />

      {/* Component-Group swap confirmation. */}
      <ConfirmDialog
        open={pendingGroupId !== null}
        title="Confirm: Switch Component Group?"
        description={
          <Box>
            Switching component group discards your current specifications. The
            parameter rows will be replaced with the defaults for the new group.
          </Box>
        }
        confirmLabel="Confirm"
        onConfirm={confirmGroupSwap}
        onCancel={cancelGroupSwap}
      />
    </>
  );
}

/**
 * SB2-CC-03 — inline Component Group select that consumes the
 * `<ReadOnlyModal>` context. Extracted so the `useReadOnlyContext` hook is
 * at component scope.
 */
function ComponentGroupInlineSelect({
  componentGroups,
  selected,
  onChange,
}: {
  componentGroups: readonly ComponentGroup[];
  selected: ComponentGroup | null;
  onChange: (next: ComponentGroup | null) => void;
}): ReactElement {
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<ComponentGroup>
      options={[...componentGroups]}
      value={selected}
      readOnly={readOnly}
      getOptionLabel={(o) => o.name.en ?? o.groupCode}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, next) => {
        onChange(next);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label="Component Group"
            required
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Component Group",
                "aria-required": true,
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}

function AuditPersonField({
  label,
  person,
}: {
  label: string;
  person: { id: string; displayName: string | null } | null;
}): ReactElement {
  if (person?.displayName == null) {
    return (
      <TextField
        label={label}
        value="Unknown"
        variant="standard"
        slotProps={{
          htmlInput: { readOnly: true, tabIndex: -1 },
          input: { sx: { fontStyle: "italic" } },
        }}
        fullWidth
      />
    );
  }
  return (
    <TextField
      label={label}
      value={person.displayName}
      variant="standard"
      slotProps={{
        htmlInput: { readOnly: true, tabIndex: -1 },
      }}
      fullWidth
    />
  );
}
